import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import CauseSelector from "@/components/charity/CauseSelector";
import { useSeedTestCampaign } from "@/hooks/useCampaignTester";
import { useCollections } from "@/hooks/useCollections";
import { useGroups } from "@/hooks/useGroups";
import { useOrganizations } from "@/hooks/useOrganizations";
import type { CauseSelection } from "@/interfaces/charity";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { formatUsd } from "@/utils/number-format";

const EXPIRY_OPTIONS = [30, 60, 90, 365];

/**
 * Seed a campaign. SCAFFOLDING.
 *
 * The collection drives the budget: its `priceMax` is the credits per contact, exactly as the
 * invoice line-item picker uses it, so a seeded campaign is costed like a real one. International
 * collections are filtered out for the same reason they are on that picker.
 */
export default function SeedCampaignDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: collections = [] } = useCollections();
  const seed = useSeedTestCampaign();

  const [title, setTitle] = useState("");
  // GFG staff are not inside a client org, so the org is chosen first and the groups follow it —
  // the same two-step the finance filters use.
  const [orgId, setOrgId] = useState<number | "">("");
  const [groupId, setGroupId] = useState<number | "">("");
  const { data: orgList } = useOrganizations();
  const { data: groups = [] } = useGroups(orgId === "" ? 0 : Number(orgId));
  const organizations = orgList?.data ?? [];
  const [collectionId, setCollectionId] = useState("");
  const [contactCount, setContactCount] = useState(5);
  const [expiryDays, setExpiryDays] = useState(60);
  const [causes, setCauses] = useState<CauseSelection[]>([]);
  const [rollover, setRollover] = useState(false);
  const [notificationMode, setNotificationMode] = useState<"gfg" | "manual">("gfg");
  const [funding, setFunding] = useState<"prepaid" | "automated" | "postpaid">("prepaid");
  // Postpaid checkout choices. The seed launches for real, so these reach the same launch the
  // builder's checkout calls.
  const [applyExistingCredits, setApplyExistingCredits] = useState(true);
  const [depositMode, setDepositMode] = useState<"credits" | "card" | "invoice">("credits");
  // Whole percentages, blank = the platform defaults the seeder holds. Kept as strings so the
  // fields can legitimately be empty rather than snapping to 0.
  const [minDepositPc, setMinDepositPc] = useState("");
  const [maintenanceMarginPc, setMaintenanceMarginPc] = useState("");
  const [topupStepPc, setTopupStepPc] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Gift collections only. `signature_collections.type` has a third value beyond domestic and
  // international — `charity`, the $5/$10 Charity Giving collections — and those are donation
  // catalogues, not gift budgets: offering one here would build a campaign at $5 per recipient
  // against charity products. The invoice picker excludes only "international", which was right
  // before charity collections existed.
  //
  // Written as two exclusions rather than `=== "domestic"` because Collection.category is typed
  // `FilterType` ("all" | "charity" | "international"), which does not include "domestic" even
  // though the column holds it — a mismatch in the shared type, not worth widening from here.
  const domestic = useMemo(
    () => collections.filter((c) => c.category !== "charity" && c.category !== "international"),
    [collections],
  );
  const collection = domestic.find((c) => c.id === collectionId);
  const perContact = collection?.priceMax ?? 0;
  const budget = perContact * contactCount;

  function submit() {
    setError(null);
    if (!title.trim()) return setError("Give the campaign a title.");
    if (groupId === "") return setError("Pick a group.");
    if (!collectionId) return setError("Pick a collection — its price is the budget per contact.");
    // Causes are optional on the wire: omitted, the server picks a real product variant so the
    // tester still works before anyone has chosen. Choosing here exercises the real path.
    seed.mutate(
      {
        title: title.trim(),
        groupId: Number(groupId),
        collectionId,
        contactCount,
        expiryDays,
        ...(causes.length > 0 ? { causes } : {}),
        rollover,
        notificationMode,
        funding,
        ...(funding === "automated" && minDepositPc ? { minDepositPc: Number(minDepositPc) } : {}),
        ...(funding === "automated" && maintenanceMarginPc
          ? { maintenanceMarginPc: Number(maintenanceMarginPc) }
          : {}),
        ...(funding === "automated" && topupStepPc ? { topupStepPc: Number(topupStepPc) } : {}),
        ...(funding === "postpaid" ? { applyExistingCredits, depositMode } : {}),
      },
      {
        onSuccess: () => onClose(),
        // The API's own sentence — e.g. "Group 5: requested 2500 credits, only 1400 available",
        // which is the difference between a usable refusal and "status code 422".
        onError: (e: unknown) => setError(apiErrorMessage(e, "Couldn't seed the campaign.")),
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 22 }}>
        Seed campaign
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "var(--color-graphite)", mb: 2 }}>
          Creates a campaign, its contacts and its budget — the state a real launch would leave
          behind. No campaign-builder involvement.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Campaign title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Diwali gifting — 25 recipients"
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Organization"
            value={orgId}
            onChange={(e) => {
              setOrgId(Number(e.target.value));
              setGroupId(""); // groups are scoped to the org
            }}
            size="small"
            fullWidth
          >
            {organizations.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Target group"
            value={groupId}
            onChange={(e) => setGroupId(Number(e.target.value))}
            size="small"
            fullWidth
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Collection"
            value={collectionId}
            onChange={(e) => setCollectionId(e.target.value)}
            size="small"
            fullWidth
            helperText="Domestic gift collections. Its price is the credits per contact."
          >
            {domestic.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title} {c.priceMax ? `— ${formatUsd(c.priceMax)}` : ""}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Contacts"
              value={contactCount}
              onChange={(e) => setContactCount(Number(e.target.value))}
              size="small"
              sx={{ flex: 1 }}
            >
              {/* Past 5 because pay-as-you-give needs 20 recipients before its schedule is real:
                  a top-up buys a percentage of the campaign in whole gifts, so below that the step
                  is under one gift and the arithmetic is the floor rather than the rate. */}
              {[1, 2, 3, 4, 5, 10, 20, 25, 40, 50].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Expiry window"
              value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))}
              size="small"
              sx={{ flex: 1 }}
              helperText="Drives commitment expiry."
            >
              {EXPIRY_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d === 365 ? "12 months" : `${d} days`}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            select
            label="Recipient notification"
            value={notificationMode}
            onChange={(e) => setNotificationMode(e.target.value as "gfg" | "manual")}
            size="small"
            helperText={
              notificationMode === "gfg"
                ? "We email each link. Credits are committed the moment these contacts are created — the send may be months away. Each recipient's window runs from their own send date."
                : "The client distributes the links. Credits are committed the same way, but with no send moment to measure from the windows end with the CAMPAIGN, on its expiry date."
            }
          >
            <MenuItem value="gfg">GiftForward sends the gift emails</MenuItem>
            <MenuItem value="manual">I'll send gift links manually</MenuItem>
          </TextField>

          {/* The funding model. Everything below the picker is the maintenance-margin model's
              opening terms, which no other model reads — so they appear only when it is chosen. */}
          <TextField
            select
            label="Funding model"
            value={funding}
            onChange={(e) => setFunding(e.target.value as "prepaid" | "automated" | "postpaid")}
            size="small"
            helperText={
              funding === "prepaid"
                ? "The whole budget is reserved from the group's credits at launch."
                : funding === "automated"
                  ? "Pay-as-you-give: only a deposit is reserved, and the sweep tops the campaign back up as gifts are redeemed."
                  : `${FUNDING_MODEL_NAME.postpaid}: a deposit at launch, and what is claimed past it is invoiced after close. The organisation must be allowed ${FUNDING_MODEL_NAME.postpaid}, with a billing contact on this group.`
            }
          >
            <MenuItem value="prepaid">Prepaid — reserve the whole budget</MenuItem>
            <MenuItem value="automated">Pay-as-you-give — deposit + automatic top-ups</MenuItem>
            <MenuItem value="postpaid">
              {FUNDING_MODEL_NAME.postpaid} — deposit now, invoice after close
            </MenuItem>
          </TextField>

          {funding === "postpaid" && (
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Existing credits"
                value={applyExistingCredits ? "apply" : "keep"}
                onChange={(e) => setApplyExistingCredits(e.target.value === "apply")}
                size="small"
                fullWidth
                helperText="Put the group's wallet toward the deposit."
              >
                <MenuItem value="apply">Apply to the deposit</MenuItem>
                <MenuItem value="keep">Don't apply</MenuItem>
              </TextField>
              <TextField
                select
                label="Rest of the deposit"
                value={depositMode}
                onChange={(e) => setDepositMode(e.target.value as "credits" | "card" | "invoice")}
                size="small"
                fullWidth
                helperText={
                  depositMode === "credits"
                    ? "Only when the wallet covers the whole deposit."
                    : depositMode === "card"
                      ? "Charges the group's default card — Stripe test mode."
                      : "Raises and sends a deposit invoice — Stripe test mode."
                }
              >
                <MenuItem value="credits">Credits</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="invoice">Invoice</MenuItem>
              </TextField>
            </Stack>
          )}

          {funding === "automated" && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Min deposit %"
                value={minDepositPc}
                onChange={(e) => setMinDepositPc(e.target.value.replace(/[^0-9.]/g, ""))}
                size="small"
                fullWidth
                placeholder="40"
                helperText="Of the obligation. The opening deposit — must exceed the trigger."
              />
              <TextField
                label="Trigger %"
                value={maintenanceMarginPc}
                onChange={(e) => setMaintenanceMarginPc(e.target.value.replace(/[^0-9.]/g, ""))}
                size="small"
                fullWidth
                placeholder="20"
                helperText="Of what is LEFT TO REDEEM, floored at 2 gifts. Fires a call."
              />
              <TextField
                label="Top-up step %"
                value={topupStepPc}
                onChange={(e) => setTopupStepPc(e.target.value.replace(/[^0-9.]/g, ""))}
                size="small"
                fullWidth
                placeholder="10"
                helperText="Of the APPROVED budget. What one call buys."
              />
            </Stack>
          )}

          <Box sx={{ borderTop: "1px solid var(--color-card-border)", pt: 2 }}>
            <CauseSelector
              value={causes}
              onChange={setCauses}
              rollover={rollover}
              onRolloverChange={setRollover}
            />
          </Box>

          {collection && funding === "postpaid" && (
            <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
              Budget <strong>{budget.toLocaleString()}</strong> credits — {contactCount} ×{" "}
              {perContact}. The seed <strong>launches</strong> the campaign through the real
              checkout: the deposit is funded now, and the campaign is scheduled to send after it
              closes, so no gift email ever goes out. Contacts start <em>ready</em> — drive them
              from here. Launch sends one "Campaign Scheduled" email, to you.
            </Alert>
          )}
          {collection && funding !== "postpaid" && (
            <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
              Budget <strong>{budget.toLocaleString()}</strong> credits — {contactCount} ×{" "}
              {perContact}. Launch verifies the group can cover it and commits every contact{" "}
              <strong>now</strong> — a prepaid campaign commits when the contacts go on the books,
              not when the link goes out.{" "}
              {notificationMode === "gfg"
                ? "Each recipient's window runs from their own send date."
                : "With no send moment, every window ends on the campaign's expiry date."}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={submit} variant="contained" disabled={seed.isPending}>
          {seed.isPending ? "Seeding…" : "Seed campaign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
